/**
 * 趋势分析模块
 * 设计稿布局：
 * - 用一个 CardCommon 包裹三个图表
 * - 上部：分举报类型声量（支持选中高亮和联动）
 * - 下部：左侧分子话题声量 + 右侧关键词变化
 * - 选中 ReportType 的 series 后，联动过滤 SubTopic 和 Keyword
 */
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import CardCommon from '@op/components/CardCommon';
import ReportTypeTrend from './ReportTypeTrend';
import SubTopicTrend from './SubTopicTrend';
import KeywordAnalysis, { ISelectedSubTopic } from './KeywordAnalysis';
import './index.scss';
import { Col, Row } from 'tea-component';



interface ITrendAnalysisProps {
  filter: any;
}

const TrendAnalysis: React.FC<ITrendAnalysisProps> = ({ filter }) => {
  // 联动状态：选中的举报类型
  const [selectedReportType, setSelectedReportType] = useState<string | null>(
    null,
  );
  // 联动状态：选中的子话题（包含 category 和 sub_category）
  const [selectedSubTopic, setSelectedSubTopic] =
    useState<ISelectedSubTopic | null>(null);

  // 处理举报类型图表点击 - 切换选中状态
  const handleReportTypeClick = useCallback((reportType: string) => {
    setSelectedReportType((prev) => (prev === reportType ? null : reportType));
    // 清除子话题选中状态
    setSelectedSubTopic(null);
  }, []);

  // 处理子话题图表点击 - 切换选中状态
  const handleSubTopicClick = useCallback(
    (name: string, category: string, sub_category: string) => {
      setSelectedSubTopic((prev) =>
        prev?.name === name ? null : { name, category, sub_category },
      );
    },
    [],
  );

  // 清除选中状态
  const handleClearSelection = useCallback(() => {
    setSelectedReportType(null);
    setSelectedSubTopic(null);
  }, []);

  useEffect(() => {
    handleClearSelection();
  }, [filter]);

  const realFilter = useMemo(() => {
    if (selectedReportType) {
      const { categories, ...dataFilter } = filter;

      return {
        ...dataFilter,
        categories: [
          {
            category: selectedReportType.toLocaleLowerCase(),
            sub_categories: [],
          },
        ],
      };
    }
    return filter;
  }, [filter, selectedReportType]);

  // 关键词图表的过滤参数（支持子话题联动）
  const keywordFilter = useMemo(() => {
    if (selectedSubTopic) {
      const { category, sub_category } = selectedSubTopic;
      return {
        ...realFilter,
        categories: [
          {
            category: category?.toLocaleLowerCase(),
            sub_categories: [sub_category?.toLocaleLowerCase()],
          },
        ],
      };
    }
    return realFilter;
  }, [realFilter, selectedSubTopic]);

  return (
    <CardCommon className="dbo-trend-analysis-cardCommon">
      {/* 第一行：分举报类型声量（支持选中高亮） */}
      <Row>
        <Col>
          <ReportTypeTrend
            filter={filter}
            selectedType={selectedReportType}
            onTypeClick={handleReportTypeClick}
          />
        </Col>
      </Row>
      {/* 第二行：分子话题声量 + 关键词变化（左右布局） */}
      <Row gap={20} className="dbo-row-sub-topic-keywords">
        <Col>
          <SubTopicTrend
            filter={realFilter}
            selectedSubTopic={selectedSubTopic?.name || null}
            onSubTopicClick={handleSubTopicClick}
          />
        </Col>
        <Col>
          <KeywordAnalysis selectedSubTopic={selectedSubTopic} filter={keywordFilter} />
        </Col>
      </Row>
    </CardCommon>
  );
};

export default TrendAnalysis;
